import { computed } from 'vue'
import type { NavigationDrawerSection } from '@/apps/core'
import { useSessionStore } from '@/apps/session/stores/session'
import { PublicFlags, type OrgPermission } from '@/types/permissions'
import type { CustomNavigationSectionsProvider } from './config'
import { useOrgExportHistory } from './exportHistory'

/**
 * The organization entries Videas adds to the settings drawer: the media players, for whoever
 * manages the organization's settings; the exports, for whoever holds `assets.library.export` in an
 * organization carrying `assets.library_exports` — or, without it, one that has exported before,
 * since its log is read either way (`./exportHistory.ts`); and
 * the ONISEP partners and IDEO cards, for any member of an organization carrying
 * `onisep.enabled` — the flag as `GET /access` resolved it for this organization
 * (`org.features`), which is where an operator's per-organization override shows and the platform
 * bootstrap never does.
 *
 * **The money has a section of its own**, offered to a holder of
 * `tenancy.billing.manage` and to nobody else — the key every one of its routes asks, reads
 * included. It holds **one entry**, the billing page, whose four tabs — overview, consumption,
 * history, preferences — light it whichever is open (`docs/plans/billing-settings.md` S1).
 *
 * **On a platform that sells no credits the entry is the consumption** (D17, U9, S3) — billed by
 * contract or not provisioned, it has no wallet, top-up or invoice to show, and those tabs refuse
 * the navigation; but the owner's U9 (2026-10-04) shows such a tenant its volumes, so the
 * consumption tab stays, and drops every amount.
 *
 * **The workspaces list leads the organization's entries**, for a holder of
 * `tenancy.workspaces.view_all` or `.manage_all` — the keys its route asks, since it names every
 * private workspace and its owner.
 *
 * **The caller's notifications join their profile**, offered to every member — what reaches them
 * is theirs to choose — and the organization's notification defaults join the organization's
 * entries for whoever manages its settings.
 */
export const videasNavigationSections: CustomNavigationSectionsProvider = ({ organizationId, t, can }) => {
  const session = useSessionStore()
  const exportsFlag = computed(() => session.hasOrgFeature(PublicFlags.ASSETS_LIBRARY_EXPORTS))
  const exportHistory = useOrgExportHistory(organizationId, () => can('assets.library.export') && !exportsFlag.value)
  return computed(() =>
    organizationId.value ? sectionsOf(t, can, exportsFlag.value || exportHistory.value) : [],
  )
}

/** The sections themselves; `offersExports` is the flag, or the history where it is off. */
function sectionsOf(
  t: (key: string) => string,
  can: (permission: OrgPermission) => boolean,
  offersExports: boolean,
): NavigationDrawerSection[] {
  const session = useSessionStore()
  const onisep = session.hasOrgFeature(PublicFlags.ONISEP_ENABLED)
  const sections: NavigationDrawerSection[] = [
    {
      key: 'profile',
      title: t('settings.navigation.myProfile'),
      children: [{ name: 'settings:notifications', title: t('settings.navigation.notifications') }],
    },
  ]
  if (can('tenancy.billing.manage') && session.sellsCredits()) {
    // One entry for the four tabs, lit on whichever is open (`docs/plans/billing-settings.md` S1).
    sections.push({
      key: 'billing',
      title: t('settings.billing.navigation.section'),
      children: [
        {
          name: 'settings:billing',
          title: t('settings.billing.navigation.billing'),
          activeNames: [
            'settings:billing-usage',
            'settings:billing-history',
            'settings:billing-preferences',
          ],
        },
      ],
    })
  } else if (can('tenancy.billing.manage')) {
    // A platform billed by contract: the consumption alone, in volumes (U9, S3).
    sections.push({
      key: 'billing',
      title: t('settings.billing.navigation.section'),
      children: [{ name: 'settings:billing-usage', title: t('settings.billing.navigation.usage') }],
    })
  }
  const children: { name: string; title: string }[] = []
  // Every workspace of the organization, for whoever reaches them all — the two keys the list's
  // route takes, since it names private workspaces and their owners.
  if (can('tenancy.workspaces.view_all') || can('tenancy.workspaces.manage_all')) {
    children.push({
      name: 'settings:organization-workspaces',
      title: t('settings.navigation.workspaces'),
    })
  }
  if (can('tenancy.settings.manage')) {
    children.push({
      name: 'settings:organization-notification-defaults',
      title: t('settings.navigation.notificationDefaults'),
    })
    children.push({
      name: 'settings:organization-media-players',
      title: t('settings.navigation.mediaPlayers'),
    })
  }
  if (can('assets.library.export') && offersExports) {
    children.push({
      name: 'settings:organization-exports',
      title: t('settings.navigation.exports'),
    })
  }
  if (onisep) {
    children.push({
      name: 'settings:organization-partners',
      title: t('settings.navigation.partners'),
    })
    children.push({
      name: 'settings:organization-ideo-cards',
      title: t('settings.navigation.ideoCards'),
    })
  }
  if (children.length > 0) sections.push({ key: 'organization', title: '', children })
  return sections
}
